/* eslint-disable no-console */
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ClientSideConfigSelector from '../../../../recoil/cashout/ClientSideConfigSelector';
import TaxAccountAtom from '../../../../recoil/cashout/TaxAccountAtom';
import useHttpRequest from '../../../api/HttpRequest';

export const TaxFormErrorCode = {
  AlreadyStarted: 'form-already-started',
  AlreadyAccepted: 'form-already-accepted',
};

/**
 * Single source of truth for the tax account data layer.
 * Owns TaxAccountAtom, the payment provider and the tax form endpoints.
 */
export default function useTaxAccount() {
  const httpRequest = useHttpRequest();

  const taxAccount = useRecoilValue(TaxAccountAtom);
  const setTaxAccount = useSetRecoilState(TaxAccountAtom);

  const config = useRecoilValue(ClientSideConfigSelector);
  const {
    live: {
      claimReward: {
        paymentProvider,
      },
    } = {},
  } = config || {};

  const basePath = `/web-payments/cash-reward/tax/${paymentProvider}/form`;

  // PATCH — re-sync the status
  const refreshTaxAccount = useCallback(async () => {
    if (!paymentProvider) return;

    const result = await httpRequest.send(`${basePath}/status`, {
      method: 'PATCH',
      additionalHeaders: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    const tax = result?.providers?.tax || result; // fallback for backward compatibility
    if (tax) {
      setTaxAccount(tax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentProvider]);

  // POST — request a tax form registration URL.
  const requestTaxForm = useCallback(
    async (requestForce = false) => httpRequest.send(basePath, {
      method: 'POST',
      additionalHeaders: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: { requestForce },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentProvider],
  );

  return {
    taxAccount,
    paymentProvider,
    refreshTaxAccount,
    requestTaxForm,
  };
}
